/**
 * Pure billing-document completeness validator shared across the Invoicing UI,
 * Ready-to-Invoice aside, and Load Details views.
 *
 * Ensures every load has the required paperwork before it can be invoiced:
 * 1. Latest Rate Confirmation
 * 2. Proof of Delivery (POD) for every delivery stop (except cancelled TONU loads)
 * 3. Lumper Receipt when a reimbursable lumper charge exists (unless explicitly waived)
 */

export type MissingBillingDocumentType =
  | 'MISSING_RATE_CONFIRMATION'
  | 'MISSING_POD'
  | 'MISSING_LUMPER_RECEIPT';

export interface MissingBillingDocumentIssue {
  type: MissingBillingDocumentType;
  stopNumber?: number;
  facilityName?: string;
  label: string;
}

export interface LoadBillingDocumentValidation {
  isComplete: boolean;
  missingDocs: MissingBillingDocumentIssue[];
  summaryText: string;
  latestRateConfirmationUrl?: string;
  isLumperReceiptWaived: boolean;
  hasLumperCharge: boolean;
}

function extractUrl(docOrUrl: unknown): string | undefined {
  let candidate: string | undefined;
  if (typeof docOrUrl === 'string') {
    candidate = docOrUrl.trim();
  } else if (docOrUrl && typeof docOrUrl === 'object') {
    const obj = docOrUrl as { url?: unknown; fileUrl?: unknown };
    const u = typeof obj.url === 'string' ? obj.url : obj.fileUrl;
    if (typeof u === 'string') {
      candidate = u.trim();
    }
  }
  if (!candidate || !/^https?:\/\//i.test(candidate)) {
    return undefined;
  }
  return candidate;
}

/**
 * Resolves ONLY the single latest Rate Confirmation URL for a load + company view.
 * Priority:
 * 1. Highest-version entry in `view.documents.rateConfirmations`
 * 2. `view.documents.rateConfirmation`
 * 3. `load.documents.rateConfirmation`
 * 4. `load.processingFileUrl`
 */
export function getLatestRateConfirmationUrl(
  load: any,
  view?: any
): string | undefined {
  const effectiveView = view ?? load?.view;
  const vd = effectiveView?.documents ?? {};

  if (Array.isArray(vd.rateConfirmations) && vd.rateConfirmations.length > 0) {
    const validVersions = vd.rateConfirmations
      .map((item: any, idx: number) => ({
        url: extractUrl(item),
        version: typeof item?.version === 'number' ? item.version : vd.rateConfirmations.length - idx,
        idx,
      }))
      .filter((item: { url?: string }) => !!item.url);

    if (validVersions.length > 0) {
      validVersions.sort((a: any, b: any) => {
        if (b.version !== a.version) return b.version - a.version;
        return a.idx - b.idx;
      });
      return validVersions[0].url;
    }
  }

  const viewRateConUrl = extractUrl(vd.rateConfirmation);
  if (viewRateConUrl) return viewRateConUrl;

  const loadRateConUrl = extractUrl(load?.documents?.rateConfirmation);
  if (loadRateConUrl) return loadRateConUrl;

  const processingUrl = extractUrl(load?.processingFileUrl);
  if (processingUrl) return processingUrl;

  return undefined;
}

/**
 * Validates whether a load has all required billing documents to be invoiced.
 */
export function validateLoadBillingDocuments(
  load: any,
  view?: any
): LoadBillingDocumentValidation {
  const effectiveView = view ?? load?.view;
  const missingDocs: MissingBillingDocumentIssue[] = [];

  const latestRateConfirmationUrl = getLatestRateConfirmationUrl(load, effectiveView);
  if (!latestRateConfirmationUrl) {
    missingDocs.push({
      type: 'MISSING_RATE_CONFIRMATION',
      label: 'Rate Confirmation',
    });
  }

  const isCancelledTonu =
    load?.status === 'CANCELLED' ||
    effectiveView?.tonuDecision?.status === 'eligible' ||
    effectiveView?.tonuDecision?.status === 'pending';

  const stops: any[] = Array.isArray(load?.stops) ? load.stops : [];

  // 2. Check PODs for every delivery stop (skip if load was cancelled)
  if (!isCancelledTonu) {
    const deliveryStops = stops.filter((s) => {
      const rawType = String(s?.stopType ?? s?.type ?? '').toLowerCase();
      return rawType === 'delivery' || rawType === 'both';
    });
    deliveryStops.forEach((stop, index) => {
      const stopNum = stop?.stopNumber ?? index + 1;
      const podUrl =
        extractUrl(stop?.documents?.proofOfDelivery) ||
        extractUrl(stop?.podUrl) ||
        extractUrl(stop?.bolUrl);
      if (!podUrl) {
        const facilityName =
          stop?.companyName || stop?.address?.city || `Stop #${stopNum}`;
        missingDocs.push({
          type: 'MISSING_POD',
          stopNumber: stopNum,
          facilityName,
          label:
            deliveryStops.length > 1 ? `Stop #${stopNum} POD` : 'POD',
        });
      }
    });
  }

  // 3. Check Lumper Receipt when a reimbursable lumper charge exists
  const viewPricing = effectiveView?.pricing ?? {};
  const isLumperReceiptWaived =
    effectiveView?.lumperReceiptWaived === true ||
    viewPricing?.lumperReceiptWaived === true;

  const viewLumperAmount = Number(viewPricing?.lumper || 0);
  const stopsWithReimbursableLumper = stops.filter((s) => {
    const fee = Number(s?.lumperFee || 0);
    if (fee <= 0) return false;
    // If broker or shipper paid directly at the dock, carrier doesn't bill for it
    if (s?.lumperPaidBy === 'broker' || s?.lumperPaidBy === 'shipper') {
      return false;
    }
    return true;
  });

  const hasLumperCharge =
    viewLumperAmount > 0 || stopsWithReimbursableLumper.length > 0;

  if (hasLumperCharge && !isLumperReceiptWaived) {
    const hasViewLevelReceipt = Boolean(
      (typeof viewPricing?.lumperReceiptUrl === 'string' &&
        viewPricing.lumperReceiptUrl.trim().length > 0) ||
        extractUrl(effectiveView?.documents?.lumperReceipt) ||
        extractUrl(load?.documents?.lumperReceipt)
    );

    if (stopsWithReimbursableLumper.length > 0) {
      let anyStopMissingReceipt = false;
      for (const stop of stopsWithReimbursableLumper) {
        const stopReceiptUrl = extractUrl(stop?.documents?.lumperReceipt);
        if (!stopReceiptUrl && !hasViewLevelReceipt) {
          anyStopMissingReceipt = true;
          const stopNum = stop?.stopNumber;
          missingDocs.push({
            type: 'MISSING_LUMPER_RECEIPT',
            stopNumber: stopNum,
            facilityName: stop?.companyName,
            label: stopNum ? `Stop #${stopNum} Lumper Receipt` : 'Lumper Receipt',
          });
        }
      }
      if (!anyStopMissingReceipt && viewLumperAmount > 0 && !hasViewLevelReceipt) {
        const anyStopHasReceipt = stops.some((s) =>
          Boolean(extractUrl(s?.documents?.lumperReceipt))
        );
        if (!anyStopHasReceipt) {
          missingDocs.push({
            type: 'MISSING_LUMPER_RECEIPT',
            label: 'Lumper Receipt',
          });
        }
      }
    } else if (viewLumperAmount > 0 && !hasViewLevelReceipt) {
      const anyStopHasReceipt = stops.some((s) =>
        Boolean(extractUrl(s?.documents?.lumperReceipt))
      );
      if (!anyStopHasReceipt) {
        missingDocs.push({
          type: 'MISSING_LUMPER_RECEIPT',
          label: 'Lumper Receipt',
        });
      }
    }
  }

  const isComplete = missingDocs.length === 0;
  const summaryText = isComplete
    ? 'All billing documents complete'
    : `Missing: ${missingDocs.map((d) => d.label).join(', ')}`;

  return {
    isComplete,
    missingDocs,
    summaryText,
    latestRateConfirmationUrl,
    isLumperReceiptWaived,
    hasLumperCharge,
  };
}
